import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, Search, Download, Ruler, BarChart, FileText } from 'lucide-react';
import { gerarRelatorioObras } from '../utils/reportGenerator';
import { useObras } from '../context/ObrasContextSupabase';
import * as XLSX from 'xlsx';

const ListaObras = () => {
  const { obras, excluirObra, filtrarObras } = useObras();
  const [filtros, setFiltros] = useState({
    termo: '',
    status: ''
  });

  const obrasFiltradas = filtrarObras(filtros);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };
  
  // Formatar distância para exibição (km com 2 casas decimais)
  const formatarDistancia = (metros) => {
    if (!metros) return '0 km';
    return `${(metros / 1000).toFixed(2)} km`;
  };
  
  // Função para calcular a distância total em metros de todos os trechos de uma obra
  const calcularDistanciaTotal = (trechos) => {
    if (!trechos || trechos.length === 0) return 0;
    
    return trechos.reduce((total, trecho) => {
      let distancia = trecho.distanciaMetros || 0;
      if (trecho.derivacoes) {
        distancia += trecho.derivacoes.reduce((subTotal, derivacao) => 
          subTotal + (derivacao.distanciaMetros || 0), 0);
      }
      return total + distancia;
    }, 0);
  };
  
  // Formatar percentual
  const formatarPercentual = (valor) => {
    return `${valor.toFixed(2)}%`;
  };
  
  // Calcular valor total dos itens
  const calcularValorTotalItens = (itens) => {
    if (!itens || itens.length === 0) return 0;
    return itens.reduce((total, item) => total + (item.total || 0), 0);
  };
  
  // Calcular progresso geral dos itens
  const calcularProgressoGeral = (itens) => {
    if (!itens || itens.length === 0) return 0;
    
    const totalValor = calcularValorTotalItens(itens);
    if (totalValor === 0) return 0;
    
    const totalMedido = itens.reduce((total, item) => total + (item.totalMedido || 0), 0);
    return (totalMedido / totalValor) * 100;
  };
  
  // Calcular a quantidade total medida (soma das quantidades medidas de todos os itens)
  const calcularQuantidadeTotalMedida = (itens) => {
    if (!itens || itens.length === 0) return 0;
    return itens.reduce((total, item) => total + (item.quantidadeMedida || 0), 0);
  };
  
  // Função para determinar a cor da barra de progresso
  const getProgressColor = (progresso) => {
    if (progresso < 30) return '#dc3545'; // Vermelho
    if (progresso < 70) return '#ffc107'; // Amarelo
    return '#28a745'; // Verde
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Planejada':
        return 'status-planejada';
      case 'Em execução':
        return 'status-execucao';
      case 'Finalizada':
        return 'status-finalizada';
      default:
        return '';
    }
  };

  const exportarParaExcel = () => {
    const dadosExport = obrasFiltradas.map(obra => {
      const distanciaTotal = obra.trechos ? calcularDistanciaTotal(obra.trechos) : 0;
      
      return {
        'Nome da Obra': obra.nome,
        'Empreiteiro': obra.empreiteiro,
        'Valor (R$)': obra.valor,
        'Status': obra.status,
        'Data de Início': formatarData(obra.dataInicio),
        'Previsão de Término': formatarData(obra.dataPrevisaoTermino),
        'Contrato': obra.contrato || '',
        'Licitação': obra.licitacao || '',
        'Processo': obra.processo || '',
        'Gestor': obra.gestor || '',
        'Fiscais de Obras': obra.fiscaisObras || '',
        'Total Medições': calcularQuantidadeTotalMedida(obra.itens || []),
        'Extensão Total (km)': (distanciaTotal / 1000).toFixed(2),
        'Quantidade de Trechos': obra.trechos ? obra.trechos.length : 0,
        'Observações': obra.observacoes
      };
    });

    const ws = XLSX.utils.json_to_sheet(dadosExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Obras');
    XLSX.writeFile(wb, 'obras-conceicao-castelo.xlsx');
  };

  const confirmarExclusao = (id, nome) => {
    if (window.confirm(`Tem certeza que deseja excluir a obra "${nome}"?`)) {
      excluirObra(id);
    }
  };
  
  // Gerar relatório em PDF
  const gerarRelatorioPDF = () => {
    gerarRelatorioObras(obrasFiltradas, filtros);
  };

  // Função para calcular totalizadores por status
  const calcularTotalizadores = () => {
    const totais = {
      'Planejada': { obras: 0, valor: 0, distancia: 0 },
      'Em execução': { obras: 0, valor: 0, distancia: 0 },
      'Finalizada': { obras: 0, valor: 0, distancia: 0 },
      'Total': { obras: 0, valor: 0, distancia: 0 }
    };
    
    obras.forEach(obra => {
      const status = obra.status || 'Planejada';
      totais[status].obras += 1;
      totais[status].valor += parseFloat(obra.valor || 0);
      totais[status].distancia += calcularDistanciaTotal(obra.trechos || []) / 1000; // Converter para km
      
      // Adicionar ao total geral
      totais['Total'].obras += 1;
      totais['Total'].valor += parseFloat(obra.valor || 0);
      totais['Total'].distancia += calcularDistanciaTotal(obra.trechos || []) / 1000;
    });
    
    return totais;
  };
  
  const totalizadores = calcularTotalizadores();

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Lista de Obras</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="button" onClick={exportarParaExcel} style={{ backgroundColor: '#28a745' }}>
              <Download size={18} />
              Exportar Excel
            </button>
            <button className="button" onClick={gerarRelatorioPDF} style={{ backgroundColor: '#0d6efd' }}>
              <FileText size={18} />
              Relatório PDF
            </button>
          </div>
        </div>
        
        {/* Cards de Totalizadores */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '15px',
          marginBottom: '25px'
        }}>
          {/* Card Planejadas */}
          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '15px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            border: '1px solid #ffeeba',
            borderLeft: '5px solid #ffc107'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#856404', fontSize: '16px' }}>Obras Planejadas</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Quantidade:</span>
              <strong>{totalizadores['Planejada'].obras}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Valor Total:</span>
              <strong>{formatarMoeda(totalizadores['Planejada'].valor)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#666' }}>Extensão:</span>
              <strong>{totalizadores['Planejada'].distancia.toFixed(2)} km</strong>
            </div>
          </div>
          
          {/* Card Em Execução */}
          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '15px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            border: '1px solid #c3e6cb',
            borderLeft: '5px solid #28a745'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#155724', fontSize: '16px' }}>Em Execução</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Quantidade:</span>
              <strong>{totalizadores['Em execução'].obras}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Valor Total:</span>
              <strong>{formatarMoeda(totalizadores['Em execução'].valor)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#666' }}>Extensão:</span>
              <strong>{totalizadores['Em execução'].distancia.toFixed(2)} km</strong>
            </div>
          </div>
          
          {/* Card Finalizadas */}
          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '15px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            border: '1px solid #b8daff',
            borderLeft: '5px solid #007bff'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#004085', fontSize: '16px' }}>Obras Finalizadas</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Quantidade:</span>
              <strong>{totalizadores['Finalizada'].obras}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Valor Total:</span>
              <strong>{formatarMoeda(totalizadores['Finalizada'].valor)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#666' }}>Extensão:</span>
              <strong>{totalizadores['Finalizada'].distancia.toFixed(2)} km</strong>
            </div>
          </div>
          
          {/* Card Total */}
          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '15px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            border: '1px solid #d6d8db',
            borderLeft: '5px solid #6c757d'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#383d41', fontSize: '16px' }}>Total Geral</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Quantidade:</span>
              <strong>{totalizadores['Total'].obras}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Valor Total:</span>
              <strong>{formatarMoeda(totalizadores['Total'].valor)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#666' }}>Extensão:</span>
              <strong>{totalizadores['Total'].distancia.toFixed(2)} km</strong>
            </div>
          </div>
        </div>

        <div className="filters" style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '15px' }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
            <input
              type="text"
              placeholder="Buscar por nome da obra ou empreiteiro..."
              value={filtros.termo}
              onChange={(e) => handleFiltroChange('termo', e.target.value)}
              style={{ paddingLeft: '40px', width: '100%' }}
            />
          </div>
          
          <select
            value={filtros.status}
            onChange={(e) => handleFiltroChange('status', e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">Todos os status</option>
            <option value="Planejada">Planejada</option>
            <option value="Em execução">Em execução</option>
            <option value="Finalizada">Finalizada</option>
          </select>
        </div>

        {obrasFiltradas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p>Nenhuma obra encontrada com os filtros aplicados.</p>
          </div>
        ) : (
          <div className="obras-grid">
            {obrasFiltradas.map(obra => (
              <div key={obra.id} className="obra-card">
                <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
                  {/* Coluna da esquerda - Informações principais */}
                  <div style={{ flex: 2 }}>
                    <div className="obra-header">
                      <div>
                        <h3 className="obra-title">{obra.nome}</h3>
                        <span className={`obra-status ${getStatusClass(obra.status)}`}>
                          {obra.status}
                        </span>
                      </div>
                    </div>

                    <div className="obra-info" style={{ gridTemplateColumns: '1fr 1fr' }}>
                      <div className="obra-info-item">
                        <strong>Empreiteiro:</strong> {obra.empreiteiro}
                      </div>
                      <div className="obra-info-item">
                        <strong>Valor:</strong> {formatarMoeda(obra.valor)}
                      </div>
                      <div className="obra-info-item">
                        <strong>Início:</strong> {formatarData(obra.dataInicio)}
                      </div>
                      <div className="obra-info-item">
                        <strong>Previsão:</strong> {formatarData(obra.dataPrevisaoTermino)}
                      </div>
                      {obra.contrato && (
                        <div className="obra-info-item">
                          <strong>Contrato:</strong> {obra.contrato}
                        </div>
                      )}
                      {obra.licitacao && (
                        <div className="obra-info-item">
                          <strong>Licitação:</strong> {obra.licitacao}
                        </div>
                      )}
                      {obra.processo && (
                        <div className="obra-info-item">
                          <strong>Processo:</strong> {obra.processo}
                        </div>
                      )}
                      <div className="obra-info-item">
                        <strong>Total Medições:</strong> {calcularQuantidadeTotalMedida(obra.itens || [])} {obra.itens && obra.itens.length > 0 && obra.itens[0].unidade ? obra.itens[0].unidade : 'un'}
                      </div>
                    </div>
                    
                    {obra.observacoes && (
                      <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                        <strong>Observações:</strong> {obra.observacoes}
                      </div>
                    )}
                  </div>
                  
                  {/* Coluna do meio - Detalhes técnicos */}
                  <div style={{ flex: 2 }}>
                    {obra.trechos && obra.trechos.length > 0 && (
                      <div style={{ 
                        marginBottom: '15px', 
                        padding: '8px 12px', 
                        backgroundColor: '#e8f5e9', 
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <Ruler size={16} style={{ color: '#2e7d32' }} />
                        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#2e7d32' }}>
                          Extensão total: {formatarDistancia(calcularDistanciaTotal(obra.trechos))}
                        </span>
                        <span style={{ fontSize: '12px', color: '#2e7d32', marginLeft: 'auto' }}>
                          {obra.trechos.length} trecho(s)
                        </span>
                      </div>
                    )}
                    
                    {obra.itens && obra.itens.length > 0 && (
                      <div>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '5px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <BarChart size={16} style={{ color: '#0d6efd' }} />
                            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#0d6efd' }}>
                              Progresso: {formatarPercentual(calcularProgressoGeral(obra.itens))}
                            </span>
                          </div>
                          <span style={{ fontSize: '12px', color: '#0d6efd' }}>
                            {obra.itens.length} item(ns)
                          </span>
                        </div>
                        
                        {/* Barra de Progresso */}
                        <div style={{ 
                          width: '100%', 
                          height: '10px', 
                          backgroundColor: '#e9ecef',
                          borderRadius: '5px',
                          overflow: 'hidden'
                        }}>
                          <div style={{ 
                            width: `${calcularProgressoGeral(obra.itens)}%`, 
                            height: '100%', 
                            backgroundColor: getProgressColor(calcularProgressoGeral(obra.itens)),
                            borderRadius: '5px'
                          }}></div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Coluna da direita - Ações */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end' }}>
                    <div className="obra-actions" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <Link to={`/obras/editar/${obra.id}`} className="button secondary" style={{ width: '100%' }}>
                        <Edit size={16} />
                        Editar
                      </Link>
                      <Link to={`/obras/medicoes/${obra.id}`} className="button" style={{ width: '100%', backgroundColor: '#0d6efd' }}>
                        <FileText size={16} />
                        Medições
                      </Link>
                      <button 
                        className="button danger"
                        onClick={() => confirmarExclusao(obra.id, obra.nome)}
                        style={{ width: '100%' }}
                      >
                        <Trash2 size={16} />
                        Excluir
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListaObras;